import { useMemo, useState } from "react";

import branding from "./branding";
import CompletedPaperReview from "./CompletedPaperReview";
import {
  getSessionModeLabel,
} from "./completedPaperReviewUtils";
import {
  getCompletedPaperResult,
} from "./services/resultService";
import "./Dashboard.css";

function formatCompletedDate(value, language) {
  if (!value) {
    return language === "af"
      ? "Datum nie beskikbaar nie"
      : "Date unavailable";
  }

  return new Date(value).toLocaleDateString(
    language === "af" ? "af-ZA" : "en-ZA",
    {
      day: "numeric",
      month: "long",
      year: "numeric",
    }
  );
}

function Dashboard({
  language,
  userEmail,
  selectedSubject,
  onStartTutor,
  onChangeSubject,
  onLogout,
  results,
  weakTopics,
}) {
  const [paperReview, setPaperReview] =
    useState(null);
  const [reviewLoadingId, setReviewLoadingId] =
    useState(null);
  const [reviewError, setReviewError] =
    useState("");

  const orderedResults = useMemo(
    () => [...(results || [])].sort(
      (first, second) => {
        const firstTime = first.completedAt
          ? new Date(first.completedAt).getTime()
          : 0;
        const secondTime = second.completedAt
          ? new Date(second.completedAt).getTime()
          : 0;

        return secondTime - firstTime ||
          Number(second.resultId || 0) -
          Number(first.resultId || 0);
      }
    ),
    [results]
  );

  const totalScorePercentage =
    orderedResults.length > 0
      ? Math.round(
        orderedResults.reduce(
          (total, result) =>
            total + Number(result.percentage || 0),
          0
        ) / orderedResults.length
      )
      : 0;

  const handleOpenResult = async (resultId) => {
    setReviewError("");
    setReviewLoadingId(resultId);

    try {
      const completedPaper =
        await getCompletedPaperResult(resultId);

      setPaperReview(completedPaper);
    } catch (error) {
      setReviewError(
        error.message ||
        (language === "af"
          ? "Die voltooide vraestel kon nie gelaai word nie."
          : "The completed paper could not be loaded.")
      );
    } finally {
      setReviewLoadingId(null);
    }
  };

  if (paperReview) {
    return (
      <CompletedPaperReview
        language={language}
        paperReview={paperReview}
        onBack={() => setPaperReview(null)}
      />
    );
  }

  return (
    <main className="dashboard-page">
      <header className="dashboard-header">
        <div className="dashboard-brand">
          <img
            src="/ScienceTutor.png"
            alt="Science Tutor logo"
          />
          <h1>{branding.platformName}</h1>
        </div>

        <div className="dashboard-actions">
          <button
            type="button"
            className="dashboard-button dashboard-button-primary"
            onClick={onChangeSubject}
          >
            {language === "af"
              ? "Verander vak"
              : "Change subject"}
          </button>

          {onLogout && (
            <button
              type="button"
              className="dashboard-button dashboard-button-outline"
              onClick={onLogout}
            >
              {language === "af" ? "Teken uit" : "Logout"}
            </button>
          )}
        </div>

        <p className="dashboard-welcome">
          {language === "af" ? "Welkom terug" : "Welcome back"}: {" "}
          <strong>{userEmail}</strong>
        </p>

        <h2 className="dashboard-subject-title">
          {selectedSubject.icon}{" "}
          {selectedSubject.name[language]}
          {selectedSubject.focus[language]
            ? ` — ${selectedSubject.focus[language]}`
            : ""}
        </h2>
      </header>

      <section className="dashboard-grid">
        <article className="dashboard-card dashboard-start-card">
          <h3>
            🎓 {language === "af" ? "Begin Tutor" : "Start Tutor"}
          </h3>

          <p>
            {language === "af"
              ? `Begin jou ${selectedSubject.name.af}-tutorsessie.`
              : `Start your ${selectedSubject.name.en} tutoring session.`}
          </p>

          {!selectedSubject.tutorAvailable && (
            <p className="dashboard-unavailable">
              {language === "af"
                ? "Hierdie tutor word tans voorberei."
                : "This tutor is currently being prepared."}
            </p>
          )}

          <button
            type="button"
            className="dashboard-button dashboard-button-primary"
            onClick={onStartTutor}
            disabled={!selectedSubject.tutorAvailable}
          >
            {language === "af" ? "Begin" : "Start"}
          </button>
        </article>

        <article className="dashboard-card dashboard-previous-card">
          <h3>
            📚 {language === "af"
              ? "Vorige Vraestelle"
              : "Previous Papers"}
          </h3>

          {reviewError && (
            <p className="dashboard-review-error">
              {reviewError}
            </p>
          )}

          {orderedResults.length > 0 ? (
            <div className="dashboard-paper-grid">
              {orderedResults.map((result, index) => (
                <div
                  className="dashboard-paper-item"
                  key={result.resultId || index}
                >
                  <div className="dashboard-paper-heading">
                    <strong>
                      {language === "af" ? "Vraestel" : "Paper"}{" "}
                      {index + 1}
                    </strong>

                    <button
                      type="button"
                      className="dashboard-score-button"
                      onClick={() =>
                        handleOpenResult(result.resultId)
                      }
                      disabled={
                        reviewLoadingId === result.resultId
                      }
                    >
                      {reviewLoadingId === result.resultId
                        ? language === "af"
                          ? "Laai..."
                          : "Loading..."
                        : `${language === "af" ? "Punt" : "Score"}: ${Math.round(result.percentage)}%`}
                    </button>
                  </div>

                  <div className="dashboard-paper-mode">
                    {getSessionModeLabel(
                      result.selectionType,
                      language
                    )}
                  </div>

                  <div className="dashboard-paper-details">
                    <span>
                      {formatCompletedDate(
                        result.completedAt,
                        language
                      )}
                    </span>
                    <span>
                      {result.marksAwarded}/
                      {result.marksAvailable}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="dashboard-empty-message">
              {language === "af"
                ? "Nog geen vraestelle nie."
                : "No papers yet."}
            </p>
          )}
        </article>

        <article className="dashboard-card dashboard-improvement-card">
          <h3>
            🎯 {language === "af"
              ? "Areas vir Verbetering"
              : "Areas for Improvement"}
          </h3>

          <div className="dashboard-performance">
            <strong>
              {language === "af"
                ? "Algehele Prestasie"
                : "Overall Performance"}
            </strong>
            <span>{totalScorePercentage}%</span>
          </div>

          <h4 className="dashboard-weak-heading">
            {language === "af"
              ? "Swak Onderwerpe"
              : "Weak Topics"}
          </h4>

          {weakTopics && weakTopics.length > 0 ? (
            weakTopics.map((topic, index) => (
              <div
                className="dashboard-weak-topic"
                key={topic.weakTopicId || index}
              >
                <div className="dashboard-weak-topic-heading">
                  <strong>{topic.topicName}</strong>
                  <span>
                    {Math.round(topic.averagePercentage)}%
                  </span>
                </div>
                <div className="dashboard-weak-topic-count">
                  {language === "af"
                    ? `${topic.correctAnswersCount} korrek uit ${topic.attemptsCount}`
                    : `${topic.correctAnswersCount} correct out of ${topic.attemptsCount}`}
                </div>
              </div>
            ))
          ) : (
            <p className="dashboard-empty-message">
              {language === "af"
                ? "Geen swak onderwerpe is tans geïdentifiseer nie."
                : "No weak topics are currently identified."}
            </p>
          )}
        </article>
      </section>
    </main>
  );
}

export default Dashboard;
